// web/src/pages/SharedConversationPage.tsx
import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { MessageSquare, LogIn, GitBranch, AlertCircle } from 'react-feather';
import { useAuth } from '../contexts/AuthContext';
import ChatMessage from '../components/ChatMessage';

interface Message {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: string;
}

interface SharedConversation {
  id: string;
  title: string;
  messages: Message[];
  createdAt: string;
}

const SharedConversationPage: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();

  const [conversation, setConversation] = useState<SharedConversation | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [forking, setForking] = useState(false);

  useEffect(() => {
    const fetchConversation = async () => {
      try {
        const response = await fetch(`/api/shared/${token}`);
        if (!response.ok) {
          throw new Error('Conversation not found');
        }
        const data = await response.json();
        setConversation(data.data);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load');
      } finally {
        setLoading(false);
      }
    };

    fetchConversation();
  }, [token]);

  const handleFork = async () => {
    if (!isAuthenticated) {
      navigate('/login?redirect=' + encodeURIComponent(window.location.pathname));
      return;
    }

    setForking(true);
    try {
      const response = await fetch(`/api/shared/${token}/fork`, {
        method: 'POST',
        credentials: 'include'
      });

      if (!response.ok) {
        throw new Error('Failed to fork conversation');
      }

      const data = await response.json();
      navigate(`/chat?conversation=${data.data.id}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Fork failed');
    } finally {
      setForking(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-eddy-blue border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-gray-600">Loading conversation...</p>
        </div>
      </div>
    );
  }

  if (error || !conversation) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center max-w-md mx-4">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <AlertCircle size={32} className="text-red-500" />
          </div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Conversation Not Found</h2>
          <p className="text-gray-600 mb-6">
            This shared conversation may have been removed or the link is invalid.
          </p>
          <button
            onClick={() => navigate('/')}
            className="px-4 py-2 bg-eddy-blue text-white rounded-lg hover:bg-eddy-primary transition-colors"
          >
            Go to Home
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      {/* Header Banner */}
      <header className="bg-eddy-blue text-white flex-shrink-0">
        <div className="max-w-4xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <MessageSquare size={20} />
            <span className="font-medium">Shared Conversation</span>
          </div>

          {isAuthenticated ? (
            <button
              onClick={handleFork}
              disabled={forking}
              className="flex items-center gap-2 px-4 py-2 bg-white/20 hover:bg-white/30
                         rounded-lg text-sm font-medium transition-colors disabled:opacity-50"
            >
              <GitBranch size={16} />
              {forking ? 'Creating copy...' : 'Continue this conversation'}
            </button>
          ) : (
            <button
              onClick={() => navigate('/login?redirect=' + encodeURIComponent(window.location.pathname))}
              className="flex items-center gap-2 px-4 py-2 bg-white/20 hover:bg-white/30
                         rounded-lg text-sm font-medium transition-colors"
            >
              <LogIn size={16} />
              Sign in to continue
            </button>
          )}
        </div>
      </header>

      {/* Conversation Title */}
      <div className="bg-white border-b border-gray-200 flex-shrink-0">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <h1 className="text-xl font-semibold text-gray-900">{conversation.title}</h1>
          <p className="text-sm text-gray-500 mt-1">
            {new Date(conversation.createdAt).toLocaleDateString(undefined, {
              year: 'numeric',
              month: 'long',
              day: 'numeric'
            })}
          </p>
        </div>
      </div>

      {/* Messages - uses shared ChatMessage component */}
      <div className="flex-1 overflow-y-auto">
        <div className="w-full">
          <div className="chat-messages-inner">
            {conversation.messages.map(msg => (
              <ChatMessage
                key={msg.id}
                id={msg.id}
                role={msg.role}
                content={msg.content}
                showMetadata={false}
                showSources={false}
              />
            ))}
          </div>
        </div>
      </div>

      {/* Footer CTA */}
      <div className="bg-white border-t border-gray-200 flex-shrink-0">
        <div className="max-w-4xl mx-auto px-4 py-4 text-center">
          {isAuthenticated ? (
            <p className="text-sm text-gray-600">
              Want to continue this conversation?{' '}
              <button
                onClick={handleFork}
                className="text-eddy-blue hover:underline font-medium"
              >
                Create your own copy
              </button>
            </p>
          ) : (
            <p className="text-sm text-gray-600">
              <button
                onClick={() => navigate('/login?redirect=' + encodeURIComponent(window.location.pathname))}
                className="text-eddy-blue hover:underline font-medium"
              >
                Sign in
              </button>
              {' '}to continue this conversation
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default SharedConversationPage;
